import { useEffect } from "react";
import {
  AuthService,
  AUTH_ZONES,
  CURRENT_AUTH_ZONE,
  HAS_REFRESH_TOKEN,
} from "@rshbintech.dboul/http";
import { LocalizationProvider } from "@rshbintech.dboul/utils";
import { Loader } from "../common/components/loader";
import { SnowPlowAnalyticsProvider } from "../common/snow-plow-analytics";
import { AutoLogout } from "./auto-logout";
import { LogoutPage } from "./logout-page";
import {
  checkIsLoginPage,
  checkIsLoginZonePage,
  redirectToNextOrMainPage,
  checkIsChooseOrganizationPage,
  checkIsMiniLk,
  checkIsRequiredSoftwarePage,
  redirectToChooseOrganizationPage,
  redirectToMiniLkPage,
  checkIsRedirectOnAppStorePage,
} from "./utils";
import { AuthProviderProps } from "./types";
import { translations } from "./translations";

/**
 * INFO: Дополнительная инициализация PerformanceTracker на уровне компонента AuthProvider
 * связана с тем, что компонент AppLayout не используется в проекте Логина,
 * что не позволит получить данные о метриках производительности данного проекта.
 */
// ** PerformanceTracker.init();

export const AuthProvider = ({ language, children }: AuthProviderProps) => {
  const isLoginPage = checkIsLoginPage();
  const isLoginZonePage = checkIsLoginZonePage();

  const isChooseOrganizationPage = checkIsChooseOrganizationPage();
  const isMiniLkPage = checkIsMiniLk();
  const isRequiredSoftwarePage = checkIsRequiredSoftwarePage();
  const isRedirectOnAppStorePage = checkIsRedirectOnAppStorePage();

  const isLoginZonePageAndUserHasRefreshToken =
    isLoginZonePage && AuthService.hasRefreshToken;

  const isNotLoginZonePageAndUserHasNotRefreshToken =
    !isLoginZonePage && !AuthService.hasRefreshToken;

  useEffect(() => {
    /**
     * INFO: Данная функциональность необходима для синхронизации
     * всех открытых вкладок пользователя в рамках одного бразуера.
     */
    const storageEventListener = (event: StorageEvent) => {
      /**
       * INFO: Флаг hasCurrentAuthZone говорит о том, что мы должны выполнять логику редиректов на основании
       * значения currentAuthZone в local storage для работы по сценарию взаимодействия с МС Аутентифкации.
       */
      if (AuthService.hasCurrentAuthZone) {
        if (event.key !== CURRENT_AUTH_ZONE) {
          return;
        }

        if (
          event.oldValue === null &&
          event.newValue === AUTH_ZONES.CHOOSE_ORGANIZATION_PAGE &&
          isLoginPage
        ) {
          redirectToChooseOrganizationPage();
        }

        if (
          (event.oldValue === AUTH_ZONES.CHOOSE_ORGANIZATION_PAGE ||
            event.oldValue === AUTH_ZONES.MAIN_PAGE) &&
          event.newValue === null &&
          !isLoginPage
        ) {
          AuthService.redirectToLoginPage();
        }

        if (
          event.oldValue === AUTH_ZONES.MAIN_PAGE &&
          event.newValue === AUTH_ZONES.CHOOSE_ORGANIZATION_PAGE &&
          !isChooseOrganizationPage
        ) {
          redirectToChooseOrganizationPage();
        }

        if (
          event.oldValue === AUTH_ZONES.CHOOSE_ORGANIZATION_PAGE &&
          event.newValue === AUTH_ZONES.MAIN_PAGE &&
          isChooseOrganizationPage
        ) {
          redirectToNextOrMainPage();
        }
      } else {
        /**
         * INFO: Флаг hasRefreshToken говорит о том, что мы должны выполнять логику редиректов на основании
         * соответствующего значения в local storage для работы по сценарию взаимодействия с Монолитом.
         */
        if (event.key !== HAS_REFRESH_TOKEN) {
          return;
        }

        if (
          event.oldValue === "false" &&
          event.newValue === "true" &&
          isLoginZonePage
        ) {
          redirectToNextOrMainPage();
        }

        if (
          event.oldValue === "true" &&
          event.newValue === "false" &&
          !isLoginZonePage
        ) {
          AuthService.redirectToLoginPage();
        }
      }
    };

    window.addEventListener("storage", storageEventListener);

    return () => window.removeEventListener("storage", storageEventListener);
  }, []);

  /**
   * INFO: Страница редиректа в магазины приложений является исключением из общей логики редиректов, поэтому игнорируем проверки если находимся на ней.
   */
  if (!isRedirectOnAppStorePage) {
    /**
     * INFO: Данная функциональность необходима для определения редиректа
     * если пользователь пытается войти на недоступную ему страницу.
     *
     * Например, незалогиненный пользователь пытается открыть главную страницу.
     */
    if (AuthService.hasCurrentAuthZone) {
      if (
        AuthService.currentAuthZone === AUTH_ZONES.MINI_LK_PAGE &&
        !isMiniLkPage
      ) {
        redirectToMiniLkPage();

        /**
         * INFO: Время редиректа на новую страницу может занимать длительное время.
         * Для того чтобы не отображать статический background-градиент тега body, отображаем лоадер.
         */
        return <Loader />;
      }

      if (
        AuthService.currentAuthZone === AUTH_ZONES.CHOOSE_ORGANIZATION_PAGE &&
        !isChooseOrganizationPage
      ) {
        redirectToChooseOrganizationPage();

        /**
         * INFO: Время редиректа на новую страницу может занимать длительное время.
         * Для того чтобы не отображать статический background-градиент тега body, отображаем лоадер.
         */
        return <Loader />;
      }

      if (
        AuthService.currentAuthZone === AUTH_ZONES.MAIN_PAGE &&
        (isLoginPage || isChooseOrganizationPage || isRequiredSoftwarePage)
      ) {
        redirectToNextOrMainPage();

        /**
         * INFO: Время редиректа на новую страницу может занимать длительное время.
         * Для того чтобы не отображать статический background-градиент тега body, отображаем лоадер.
         */
        return <Loader />;
      }
    } else {
      /**
       * INFO: Данный блок кода отработает если у пользователя в local storage отсутствует флаг currentAuthZone.
       */
      if (isLoginZonePageAndUserHasRefreshToken) {
        redirectToNextOrMainPage();

        /**
         * INFO: Время редиректа на новую страницу может занимать длительное время.
         * Для того чтобы не отображать статический background-градиент тега body, отображаем лоадер.
         */
        return <Loader />;
      }

      if (isNotLoginZonePageAndUserHasNotRefreshToken) {
        AuthService.setLoginPageNextParam(window.location.pathname);
        AuthService.redirectToLoginPage();

        /**
         * INFO: Время редиректа на новую страницу может занимать длительное время.
         * Для того чтобы не отображать статический background-градиент тега body, отображаем лоадер.
         */
        return <Loader />;
      }
    }
  }

  return (
    <LocalizationProvider
      resources={translations}
      fallBackLanguage="ru_RU"
      defaultNamespace="COMMON"
      language={language}
    >
      <SnowPlowAnalyticsProvider>
        {children}
        <AutoLogout />
        <LogoutPage />
      </SnowPlowAnalyticsProvider>
    </LocalizationProvider>
  );
};
